'use client';

import React, { useState } from 'react';
import Dialog from '@ui/Dialog';
import Button from '@ui/Button';
import Typography from '@ui/Typography';
import { getEnvBaseUrl } from '@/utils/stringHelpers';
import { useUser } from '@auth0/nextjs-auth0/client';

function SignupFallback() {
  const auth0Session = useUser();
  const auth0User = auth0Session.user;
  const [isOpen, setIsOpen] = useState(true);

  const closeModal = () => setIsOpen(false);

  const handleClaimNonprofit = () =>
    `${getEnvBaseUrl('admin')}/nonprofit/sign-up`;
  const handleCompanyOnboard = () => `${getEnvBaseUrl()}/onboard/company`;
  const handleIndividualOnboard = () => `${getEnvBaseUrl()}/onboard/individual`;
  const handleChangeMakerOnboard = () =>
    `${getEnvBaseUrl()}/onboard/change-maker`;

  const clearCookie = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  const handleLogout = () => {
    clearCookie('appSession');
    window.location.href = '/api/auth/logout';
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={closeModal}
      noClose={true}
      contentClass="!p-0"
      maxWidth="lg"
      titleClass="!text-center"
      title="Welcome to Neki">
      <div className="flex flex-col items-center px-4 py-6">
        <Typography variant="h4" className="mb-6 text-center font-bold">
          Choose Your Journey to Social Impact
        </Typography>

        {/* Grid Section */}
        <div className="grid w-full max-w-5xl gap-8 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {/* Dynamic Cards */}
          {[
            {
              title: 'Individuals',
              description:
                'Join a thriving community supporting nonprofits and making a difference.',
              image: '/assets/images/individual-onboard-image.jpg',
              actionText: 'Join',
              actionHref: handleIndividualOnboard(),
              bgColor: 'bg-[#FF6361]',
            },
            {
              title: 'Businesses',
              description:
                'Start a social impact initiative and engage your organization in giving back.',
              image: '/assets/images/company-onboard-image.jpg',
              actionText: 'Create',
              actionHref: handleCompanyOnboard(),
              bgColor: 'bg-[#ECA400]',
            },
            {
              title: 'Nonprofits',
              description:
                'Claim your nonprofit and connect with our global community.',
              image: '/assets/images/nonprofit-onboard-image.jpg',
              actionText: 'Claim',
              actionHref: handleClaimNonprofit(),
              bgColor: 'bg-[#BC5090]',
            },
            {
              title: 'Associations',
              description:
                'Build your own community of like-minded changemakers and create an impact',
              image: '/assets/images/change-maker-onboard-image.jpg',
              actionText: 'Build',
              actionHref: handleChangeMakerOnboard(),
              bgColor: 'bg-[#33CCCC]',
            },
          ].map(
            (
              { title, description, image, actionText, actionHref, bgColor },
              index,
            ) => (
              <div
                key={index}
                className="flex h-full flex-col items-center rounded-lg bg-white shadow-lg transition-shadow duration-300 hover:shadow-xl">
                {/* Image */}
                <img
                  src={image}
                  alt={title}
                  className="h-40 w-full rounded-t-lg object-cover"
                />

                {/* Card Content */}
                <div className="flex flex-grow flex-col justify-between p-6 text-center">
                  <div>
                    <Typography variant="h5" className="mb-4 font-semibold">
                      {title}
                    </Typography>
                    <Typography variant="body2" className="mb-6 text-gray-600">
                      {description}
                    </Typography>
                  </div>
                  <div className="flex justify-center">
                    <Button
                      as="a"
                      variant="filled"
                      color="primary"
                      href={actionHref}
                      target="_blank"
                      className={`w-auto ${bgColor} rounded-md px-6 py-3 font-medium text-white hover:opacity-90`}>
                      {actionText}
                    </Button>
                  </div>
                </div>
              </div>
            ),
          )}
        </div>

        {/* User Information */}
        <Typography variant="body1" className="mt-10 text-center">
          You’re signing up as <strong>{auth0User?.email}</strong>
        </Typography>
        <Typography
          variant="body2"
          className="mt-4 text-center text-gray-500 underline">
          Wrong account?{' '}
          <Typography
            onClick={handleLogout}
            color="hyperlink"
            className="cursor-pointer text-blue-600">
            Sign In{' '}
          </Typography>
          Instead
        </Typography>
      </div>
    </Dialog>
  );
}

export default React.memo(SignupFallback);
